import clsx from "clsx";
import Link from "next/link";
import { Card } from "../Card";
import { CardBorderTheme, CardTheme } from "../Card/types";
import { TimeLeftToPurchase } from "./components/TimeLeftToPurchase";
import { ProductCardProps } from "./types";
import { ProductCardContent } from "./components/ProductCardContent";
import { ProductCardThumbnail } from "./components/ProductCardThumbnail";
import { ProductCardFooter } from "./components/ProductCardFooter";
import { TextScreenReaderOnly } from "@/src/components/TextScreenReaderOnly";
import { useMouseStateController } from "./hooks";
import { PRODUCT_CARD } from "./testIds";

function LeftHeaderComponent(props: ProductCardProps) {
  return props.purchaseExpirationDate ? (
    <TimeLeftToPurchase purchaseExpirationDate={props.purchaseExpirationDate} />
  ) : null;
}

export function ProductCard(props: ProductCardProps) {
  const mouseController = useMouseStateController({
    enabled: !!props.videoSrc,
  });

  const borderTheme = props.isSolebound
    ? CardBorderTheme.special
    : CardBorderTheme.light;

  return (
    <article
      className={clsx(props.className, "relative flex min-w-0 flex-col group")}
      onMouseEnter={mouseController.onMouseEnter}
      onMouseLeave={mouseController.onMouseLeave}
      data-testid={PRODUCT_CARD}
    >
      {/* 
        NOTE: this link is styled to appear absolutely above all the 
        content instead of wrapping it so that we can use links inside
        the card without creating invalid markup and avoiding having
        to `e.preventDefault` when interacting with other elements 
        inside the card like buttons. 
        
        If you need to interact with an element inside the card it
        will need a z-index of 1 or higher to appear above this link.
      */}
      {props.link && (
        <Link
          onClick={props.onLinkClick}
          className="betterhover:hover:outline-green-500 betterhover:hover:outline focus:outline focus:outline-green-500 outline-2 transition-all absolute inset-0 z-1"
          href={props.link}
        >
          <TextScreenReaderOnly>
            View details of {props.tokenName}
          </TextScreenReaderOnly>
        </Link>
      )}

      <Card
        hasBorder
        theme={CardTheme.dark}
        borderTheme={borderTheme}
        className="relative flex flex-col"
      >
        <LeftHeaderComponent {...props} />
        <ProductCardThumbnail
          isHovered={mouseController.isHovered}
          {...props}
        />
        <ProductCardContent {...props} />
        <ProductCardFooter {...props} />
      </Card>
    </article>
  );
}
